import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { queryClient } from '@libs/query-client';
import { apiUrl } from '@constants/url';
import { readAccessTokenClaims } from '@utils/access-token';
import { IMPERSONATION_CHANGED_EVENT } from '@utils/impersonation-state';
import { SESSION_CLEARED_EVENT, SESSION_STARTED_EVENT } from '@utils/session-events';
import { type CmsMe, type StaffAccess, staffAccessFrom, staffIdFrom } from '@utils/staff-access';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const staffAccessQueryKeys = {
  all: ['cms', 'me'] as const,
  me: (staffId: string) => ['cms', 'me', staffId] as const,
};

// The token changes on sign-in, sign-out and impersonation start/stop, in this
// tab or another one.
const TOKEN_EVENTS = [SESSION_STARTED_EVENT, SESSION_CLEARED_EVENT, IMPERSONATION_CHANGED_EVENT, 'storage'];

const subscribeToken = (onChange: () => void) => {
  for (const event of TOKEN_EVENTS) window.addEventListener(event, onChange);
  return () => {
    for (const event of TOKEN_EVENTS) window.removeEventListener(event, onChange);
  };
};

const readStaffId = () => staffIdFrom(readAccessTokenClaims());
const readImpersonating = () => readAccessTokenClaims()?.impersonated === true;

// Starting or stopping an impersonation keeps the staff member, and so the
// cache key, but the grants are asked again anyway: a switch is the moment an
// admin is most likely to have just changed them. One listener for every
// caller of the hook.
let refetchOnImpersonationChange = false;
const listenForImpersonationChange = () => {
  if (refetchOnImpersonationChange || typeof window === 'undefined') return;
  refetchOnImpersonationChange = true;
  window.addEventListener(IMPERSONATION_CHANGED_EVENT, () => {
    void queryClient.invalidateQueries({ queryKey: staffAccessQueryKeys.all });
  });
};

/**
 * Staff powers of the signed-in person, from GET /api/v1/cms/me
 * (see utils/staff-access). Keyed by the staff member behind the token, so a
 * sign-in as someone else asks again; logout clears the query cache
 * (auth-session).
 *
 * Fails closed: while loading, without a session, or on an error, every flag
 * is false, so staff-only UI never shows before the backend has said yes.
 */
export function useStaffAccess(): StaffAccess & { isLoading: boolean } {
  listenForImpersonationChange();
  const staffId = useSyncExternalStore(subscribeToken, readStaffId, () => null);
  const impersonating = useSyncExternalStore(subscribeToken, readImpersonating, () => false);

  const { data, isLoading } = useQuery({
    queryKey: staffAccessQueryKeys.me(staffId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<CmsMe>>(apiUrl.cms.me, { signal });
      return response.data.data;
    },
    enabled: staffId !== null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return { ...staffAccessFrom(data, impersonating), isLoading };
}
